import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ADDON_PRODUCT_KIND, MarketingCharacteristics } from '@app/constants';
import { ConfirmPurchaseLoading } from '@app/components/ConfirmPurchaseLoading';
import { OrderError } from '@app/components/OrderError';
import StepNavigation, { Step } from '@app/components/StepNavigation';
import {
  purchaseAddOn,
  resetAddonConfiguration,
  selectAddOns,
  selectCurrentShopAddon,
  setEicMessageError,
  setErrorDisclosureCheck,
  setScrollToConsent,
  setShowLastStepError,
  setVoiceEquipmentError,
  VoiceConfig,
} from '@app/redux/addOns';
import { selectConfig } from '@app/redux/config';
import { selectUserInfo } from '@app/redux/userInfo';

import { VoiceAddonPhoneNumberType } from '@mfe/shared/schema-types';
import { getCharacteristicValue } from '@mfe/shared/utils';

import ConfigurationAddonError from '../../components/ConfigurationAddonError';
import { OrderSuccess } from '../../components/OrderSuccess/OrderSuccess';
import SingleAddonContent from '../../components/SingleAddonContent';
import ConfigureVoice from '../ConfigureVoice';
import Disclosure from '../Disclosure';
import { useHasFilledOutCustomAddress } from './useHasFilledOutCustomAddress';
import { CountryCode, YES } from '../../types';

const hasCompletedRequiredFields = (voiceConfig: VoiceConfig, hasFilledOutCustomAddress: boolean) =>
  voiceConfig.selectedPhoneNumber &&
  (voiceConfig.useServiceAddressForE911 || hasFilledOutCustomAddress) &&
  voiceConfig.callerId &&
  voiceConfig.directoryListingType;

const SingleAddonNavigation = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('ShopAddons');
  const { submitOrder, voiceEquipmentError, configureAddonSuccess, getEicMessageError, configureAddonError } =
    useSelector(selectAddOns);

  const currentShopAddon = useSelector(selectCurrentShopAddon);

  const eicRequired = currentShopAddon
    ? getCharacteristicValue(
        currentShopAddon.marketing_copy.translations[0].characteristics,
        MarketingCharacteristics.EXPLICIT_CONSENT_REQUIRED
      )?.toLowerCase() === YES
    : false;

  const { voiceConfig, eicChecked } = useSelector(selectAddOns);
  const { name, kind } = currentShopAddon || {};
  const {
    userInfo: {
      firstName,
      lastName,
      address: { service: serviceAddress },
    },
  } = useSelector(selectUserInfo);

  const isVoice = kind === ADDON_PRODUCT_KIND.voice;
  const { hasFilledOutCustomAddress } = useHasFilledOutCustomAddress();

  const purchaseVoicePayload = {
    productName: currentShopAddon?.id ?? '',
    shoppingCartId: currentShopAddon?.offer_id ?? '',
    productTypeId: currentShopAddon?.id ?? '',
    rootProductTypeId: currentShopAddon?.id ?? '',
    voice: JSON.stringify({
      id: currentShopAddon?.id,
      telephoneNumber: voiceConfig.selectedPhoneNumber,
      firstName,
      lastName,
      callerId: voiceConfig.callerId,
      blockCallerId: `${voiceConfig.blockCallerId || false}`,
      directoryListType: voiceConfig.directoryListingType,
      numberType: VoiceAddonPhoneNumberType.New,
      emergencyAddress: !hasFilledOutCustomAddress
        ? serviceAddress
        : {
            addressLines: [voiceConfig.emergencyAddress?.streetAddress, voiceConfig.emergencyAddress.aptUnit],
            municipality: voiceConfig.emergencyAddress.cityAddress,
            region: voiceConfig.emergencyAddress.stateProvince,
            countryCode: CountryCode.US,
            postalCode: voiceConfig.emergencyAddress.zipCode,
          },
      publishDirectoryListAddress: `${voiceConfig.publishDirectoryListAddress || false}`,
    }),
  };

  const purchasePayload = {
    productName: currentShopAddon?.id ?? '',
    shoppingCartId: currentShopAddon?.offer_id ?? '',
    productTypeId: currentShopAddon?.id ?? '',
    rootProductTypeId: currentShopAddon?.id ?? '',
  };

  if (!currentShopAddon) {
    history.replace('/ShopAddons');
  }

  const steps: Step[] = [
    {
      title: `${name}`,
      component: SingleAddonContent,
      current: true,
      displayCollapsibleCart: true,
      action: () => {
        if (!isVoice && !eicRequired) {
          dispatch(purchaseAddOn(purchasePayload));
        }
      },
    },
  ];

  if (eicRequired) {
    steps.splice(1, 0, {
      title: `${t('disclosure.titlePage')}`,
      component: Disclosure,
      current: false,
      displayCollapsibleCart: true,
      displayTC: false,
      action: () => {
        if (!eicChecked) {
          dispatch(setErrorDisclosureCheck(true));
          dispatch(setScrollToConsent(true));
          return false;
        }
        if (!isVoice && configureAddonSuccess) {
          dispatch(purchaseAddOn(purchasePayload));
        }
      },
    });
  }

  if (isVoice) {
    steps.splice(2, 0, {
      title: `${name}`,
      component: ConfigureVoice,
      current: false,
      displayCollapsibleCart: true,
      action: () => {
        const isFormValid = hasCompletedRequiredFields(voiceConfig, hasFilledOutCustomAddress);
        if (isFormValid) {
          dispatch(purchaseAddOn(purchaseVoicePayload));
        } else {
          dispatch(setShowLastStepError(true));
        }
      },
    });
  }

  useEffect(() => {
    return () => {
      dispatch(resetAddonConfiguration());
      dispatch(setVoiceEquipmentError(false));
    };
  }, [dispatch]);

  if (!currentShopAddon) {
    return null;
  }

  if (configureAddonError || getEicMessageError) {
    return (
      <ConfigurationAddonError
        title={t('disclosure.error.title')}
        description={t('disclosure.error.description')}
        handlePrimaryAction={() => dispatch(setEicMessageError(null))}
      />
    );
  }

  if (submitOrder.loading) {
    return <ConfirmPurchaseLoading title={'confirmPurchase.title'} description={'confirmPurchase.description'} />;
  }

  if (voiceEquipmentError) {
    return <OrderError title={t('voiceEquipmentError.title')} />;
  }

  if (submitOrder.error) {
    return <OrderError title={t('error.title')} />;
  }

  if (submitOrder.success) {
    return <OrderSuccess />;
  }

  return <StepNavigation steps={steps} nameSpace={'ShopAddons'} />;
};

export default SingleAddonNavigation;
